import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE, formatApiError, useAuth } from "../components/AuthContext";
import { Plus, Trash2, DollarSign, Palette, Save, X } from "lucide-react";

// Keep this in sync with backend STAGE_ROLES / models.py
const ROLES = [
  { value: "accountant",         label: "Accountant" },
  { value: "bookkeeper",         label: "Bookkeeper" },
  { value: "payroll_specialist", label: "Payroll Specialist" },
  { value: "tax_specialist",     label: "Tax Specialist" },
  { value: "admin",              label: "Admin" },
  { value: "client_manager",     label: "Client Manager" },
  { value: "partner",            label: "Partner" },
];
const COLUMN_CLASSES = ["todo", "in_progress", "review", "done"];
const INPUT = "w-full bg-white border border-[var(--border)] rounded-md px-2.5 py-1.5 text-[13px] focus:outline-none focus:border-[var(--ink)]";

/**
 * v68.9 — Workflow settings page combining:
 *   - Custom kanban statuses (per org)
 *   - Versioned role → rate cards (rate plans)
 */
export default function WorkflowSettings() {
  const { state } = useAuth();
  const canEdit = ["owner", "manager"].includes(state?.user?.role);
  return (
    <div className="px-10 py-12 max-w-[1000px]" data-testid="workflow-settings-page">
      <div className="overline mb-2">Settings</div>
      <h1 className="font-heading text-4xl mb-3" style={{ fontWeight: 500 }}>Workflow</h1>
      <p className="text-[var(--ink-2)] max-w-2xl mb-10">
        Configure the kanban statuses your firm uses on every job, and the versioned rate plans that drive
        budget-by-role figures on workflow templates.
      </p>

      <StatusesSection canEdit={canEdit} />
      <div className="h-16" />
      <RatePlansSection canEdit={canEdit} />
    </div>
  );
}

// ============================================================================
// Custom statuses
// ============================================================================
function StatusesSection({ canEdit }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);   // null | new-row form

  const reload = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/workflow-statuses`);
      setRows(data || []);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { reload(); }, []);

  const grouped = COLUMN_CLASSES.map((cc) => ({
    column: cc,
    items: rows.filter((r) => r.column_class === cc)
                .sort((a, b) => (a.position || 0) - (b.position || 0)),
  }));

  const create = async () => {
    if (!draft?.key?.trim() || !draft?.label?.trim()) {
      toast.error("Key and label are required"); return;
    }
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/workflow-statuses`, {
        key: draft.key.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_"),
        label: draft.label.trim(),
        column_class: draft.column_class,
        color: draft.color || null,
        position: Number(draft.position) || 100,
      });
      toast.success("Status added");
      setDraft(null); reload();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (row) => {
    if (row.is_default) { toast.error("Default statuses cannot be deleted"); return; }
    if (!window.confirm(`Delete status "${row.label}"?`)) return;
    try {
      await axios.delete(`${API_BASE}/workflow-statuses/${row.status_id}`);
      toast.success("Deleted"); reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <section data-testid="workflow-settings-statuses">
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="overline">Kanban statuses</div>
          <h2 className="font-heading text-2xl mt-1" style={{ fontWeight: 500 }}>Custom statuses</h2>
          <p className="text-[13px] text-[var(--ink-2)] mt-1 max-w-lg">
            The four defaults render as your kanban columns. Add extras (e.g. &ldquo;Waiting on ATO&rdquo;) that pin
            to a column and show as their own badge on job cards.
          </p>
        </div>
        {canEdit && !draft && (
          <button className="btn-primary" onClick={() => setDraft({
            key: "", label: "", column_class: "in_progress", color: "#4a5b73", position: 100,
          })} data-testid="workflow-status-add-btn">
            <Plus size={13}/> Add status
          </button>
        )}
      </div>

      {draft && (
        <div className="card p-4 mb-4 grid grid-cols-6 gap-2 items-end" data-testid="workflow-status-draft">
          <div><label className="overline block mb-1">Key</label>
            <input className={INPUT} placeholder="waiting_on_ato" value={draft.key}
              onChange={(e) => setDraft({ ...draft, key: e.target.value })}
              data-testid="workflow-status-draft-key" /></div>
          <div className="col-span-2"><label className="overline block mb-1">Label</label>
            <input className={INPUT} placeholder="Waiting on ATO" value={draft.label}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              data-testid="workflow-status-draft-label" /></div>
          <div><label className="overline block mb-1">Column</label>
            <select className={INPUT} value={draft.column_class}
              onChange={(e) => setDraft({ ...draft, column_class: e.target.value })}
              data-testid="workflow-status-draft-column">
              {COLUMN_CLASSES.map((c) => <option key={c} value={c}>{c.replace("_"," ")}</option>)}
            </select></div>
          <div><label className="overline block mb-1">Colour</label>
            <input className={INPUT} type="color" value={draft.color}
              onChange={(e) => setDraft({ ...draft, color: e.target.value })}
              data-testid="workflow-status-draft-color" /></div>
          <div className="flex gap-1">
            <button className="btn-primary flex-1 justify-center" disabled={busy} onClick={create}
              data-testid="workflow-status-draft-save">
              <Save size={12}/> Save
            </button>
            <button className="btn-ghost" onClick={() => setDraft(null)}><X size={14}/></button>
          </div>
        </div>
      )}

      <div className="card divide-y divide-[var(--border)]">
        {grouped.map((grp) => (
          <div key={grp.column} className="p-4">
            <div className="overline mb-2 text-[10px]">{grp.column.replace("_"," ")}</div>
            {grp.items.length === 0 && <div className="text-[12px] text-[var(--ink-3)]">No statuses in this column.</div>}
            <div className="space-y-1.5">
              {grp.items.map((r) => (
                <div key={r.status_id} className="flex items-center gap-3 text-[13px]"
                     data-testid={`workflow-status-row-${r.key}`}>
                  <span className="inline-block w-3 h-3 rounded-full" style={{ background: r.color || "var(--ink-3)" }}/>
                  <span className="font-medium">{r.label}</span>
                  <span className="font-mono text-[11px] text-[var(--ink-3)]">{r.key}</span>
                  {r.is_default && <span className="badge badge-outline text-[9px]">default</span>}
                  <div className="flex-1"/>
                  {!r.is_default && canEdit && (
                    <button onClick={() => remove(r)} className="text-[var(--ink-3)] hover:text-[var(--accent)]"
                      data-testid={`workflow-status-delete-${r.key}`}>
                      <Trash2 size={13}/>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ============================================================================
// Rate Plans
// ============================================================================
function RatePlansSection({ canEdit }) {
  const [rows, setRows] = useState([]);
  const [effective, setEffective] = useState(null);
  const [draft, setDraft] = useState(null);

  const reload = async () => {
    try {
      const [list, eff] = await Promise.all([
        axios.get(`${API_BASE}/rate-plans`),
        axios.get(`${API_BASE}/rate-plans/effective`),
      ]);
      setRows(list.data || []);
      setEffective(eff.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { reload(); }, []);

  const create = async () => {
    if (!draft?.name?.trim() || !draft?.start_date) {
      toast.error("Name and start date are required"); return;
    }
    try {
      await axios.post(`${API_BASE}/rate-plans`, {
        name: draft.name.trim(),
        start_date: draft.start_date,
        end_date: draft.end_date || null,
        currency: draft.currency || "AUD",
        rates: draft.rates.filter((r) => r.role && r.rate !== ""),
        notes: draft.notes || null,
      });
      toast.success("Rate plan created"); setDraft(null); reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const startDraftFromCurrent = () => {
    // Duplicate the currently effective plan, bump the FY, blank the end_date.
    const src = effective?.plan;
    const nextFY = new Date();
    nextFY.setMonth(6); nextFY.setDate(1);   // 1 July
    if (src && src.start_date >= nextFY.toISOString().slice(0, 10)) {
      nextFY.setFullYear(nextFY.getFullYear() + 1);
    }
    setDraft({
      name: src ? `${nextFY.getFullYear()} Standard AU` : "New rate plan",
      start_date: nextFY.toISOString().slice(0, 10),
      end_date: "",
      currency: src?.currency || "AUD",
      rates: src ? src.rates.map((r) => ({ ...r })) : ROLES.map((r) => ({ role: r.value, rate: 0 })),
      notes: "",
    });
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete plan "${row.name}"?`)) return;
    try {
      await axios.delete(`${API_BASE}/rate-plans/${row.plan_id}`);
      toast.success("Deleted"); reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const activate = async (row) => {
    if (!window.confirm(
      `Activate & lock "${row.name}"?\n\n` +
      `Once activated, its rates and start date CANNOT be changed. ` +
      `You'll be able to change the end_date and notes to close it out — ` +
      `for new rates, create a new plan with a future start date.`
    )) return;
    try {
      const { data } = await axios.post(`${API_BASE}/rate-plans/${row.plan_id}/activate`);
      toast.success(data.activated ? "Plan activated" : "Already activated");
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // v68.13 — Plan comparison view (side-by-side diff vs effective plan)
  const [compareOf, setCompareOf] = useState(null);   // { plan } selected for comparison

  const effectivePlanId = effective?.plan?.plan_id;

  return (
    <section data-testid="workflow-settings-rate-plans">
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="overline">Rate plans</div>
          <h2 className="font-heading text-2xl mt-1" style={{ fontWeight: 500 }}>Role → rate cards</h2>
          <p className="text-[13px] text-[var(--ink-2)] mt-1 max-w-lg">
            Versioned so &ldquo;$150 accountant / $100 bookkeeper&rdquo; can start 1 July and roll forward each year without
            touching individual users. The plan whose window contains today is treated as effective.
          </p>
        </div>
        {canEdit && !draft && (
          <button className="btn-primary" onClick={startDraftFromCurrent} data-testid="rate-plan-add-btn">
            <Plus size={13}/> New rate plan
          </button>
        )}
      </div>

      {effective?.plan && (
        <div className="card p-3 mb-4 flex items-center gap-3 text-[12px]" style={{ background: "var(--subtle)" }}
             data-testid="rate-plan-effective">
          <DollarSign size={14} className="text-[var(--accent)]"/>
          <span className="font-medium">Effective on {effective.as_of}:</span>
          <span>{effective.plan.name} · starts {effective.plan.start_date}{effective.plan.end_date ? ` → ${effective.plan.end_date}` : ""}</span>
          <span className="ml-auto text-[var(--ink-3)]">{effective.plan.currency}</span>
        </div>
      )}

      {draft && <RatePlanDraft draft={draft} setDraft={setDraft} onCancel={() => setDraft(null)} onSave={create}/>}

      <div className="space-y-3">
        {rows.map((p) => {
          const locked = !!p.activated_at && p.start_date && p.start_date <= new Date().toISOString().slice(0, 10);
          const canActivate = !p.activated_at && canEdit;
          return (
          <div key={p.plan_id} className={`card p-4 ${p.plan_id === effectivePlanId ? "border-[var(--accent)]" : ""}`}
               data-testid={`rate-plan-row-${p.plan_id}`}>
            <div className="flex items-start justify-between mb-3 gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-heading text-lg" style={{ fontWeight: 500 }}>{p.name}</h3>
                  {p.plan_id === effectivePlanId && <span className="badge badge-accent">effective</span>}
                  {locked && (
                    <span className="badge text-[9px] inline-flex items-center gap-1"
                          style={{ background: "var(--ink)", color: "var(--paper)" }}
                          data-testid={`rate-plan-locked-${p.plan_id}`}>
                      LOCKED
                    </span>
                  )}
                  {p.activated_at && !locked && (
                    <span className="badge text-[9px] inline-flex items-center gap-1"
                          style={{ background: "var(--moss)", color: "var(--paper)" }}>
                      activated
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-[var(--ink-3)] font-mono mt-0.5">
                  {p.start_date}{p.end_date ? ` → ${p.end_date}` : " · current"} · {p.currency}
                  {p.activated_at && (
                    <span className="ml-2">· activated {new Date(p.activated_at).toLocaleDateString()}</span>
                  )}
                </div>
                {p.notes && <div className="text-[12px] text-[var(--ink-2)] mt-1">{p.notes}</div>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {p.plan_id !== effectivePlanId && effective?.plan && (
                  <button onClick={() => setCompareOf(p)} className="btn-ghost text-[11px]"
                          data-testid={`rate-plan-compare-${p.plan_id}`}>
                    Compare to current
                  </button>
                )}
                {canActivate && (
                  <button onClick={() => activate(p)} className="btn-secondary text-[11px]"
                          data-testid={`rate-plan-activate-${p.plan_id}`}>
                    Activate & lock
                  </button>
                )}
                {canEdit && !locked && (
                  <button onClick={() => remove(p)} className="text-[var(--ink-3)] hover:text-[var(--accent)]">
                    <Trash2 size={13}/>
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {p.rates.map((r) => (
                <div key={r.role} className="border border-[var(--border)] rounded px-2.5 py-1.5">
                  <div className="text-[10px] text-[var(--ink-3)] uppercase tracking-wider">{r.role.replace("_"," ")}</div>
                  <div className="font-mono text-[15px] mt-0.5">${r.rate.toFixed(2)}<span className="text-[10px] text-[var(--ink-3)] ml-1">/h</span></div>
                </div>
              ))}
            </div>
          </div>
        );})}
        {rows.length === 0 && (
          <div className="card p-8 text-center text-[13px] text-[var(--ink-3)]">
            No rate plans yet. Click <em>New rate plan</em> above to create one.
          </div>
        )}
      </div>
      {compareOf && effective?.plan && (
        <PlanCompareModal a={effective.plan} b={compareOf} onClose={() => setCompareOf(null)} />
      )}
    </section>
  );
}

// v68.13 — Side-by-side diff between two rate plans.
function PlanCompareModal({ a, b, onClose }) {
  const roles = Array.from(new Set([
    ...(a.rates || []).map((r) => r.role),
    ...(b.rates || []).map((r) => r.role),
  ])).sort();
  const rateFor = (plan, role) => (plan.rates || []).find((r) => r.role === role)?.rate;
  const fmt = (v) => v == null ? "—" : `$${v.toFixed(2)}`;
  return (
    <div className="fixed inset-0 z-[70] bg-[var(--ink)]/50 flex items-center justify-center p-6"
         onClick={onClose} data-testid="plan-compare-modal">
      <div className="bg-[var(--paper)] rounded-md border border-[var(--border)] p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="overline">Rate plan · comparison</div>
            <h3 className="font-heading text-xl mt-1" style={{ fontWeight: 500 }}>
              {a.name} <span className="text-[var(--ink-3)] mx-2">vs</span> {b.name}
            </h3>
          </div>
          <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]">×</button>
        </div>
        <table className="w-full text-[13px]" data-testid="plan-compare-table">
          <thead>
            <tr className="text-left text-[var(--ink-3)] border-b border-[var(--border)]">
              <th className="py-1.5 font-normal">Role</th>
              <th className="py-1.5 font-normal text-right">{a.name}</th>
              <th className="py-1.5 font-normal text-right">{b.name}</th>
              <th className="py-1.5 font-normal text-right">Delta</th>
            </tr>
          </thead>
          <tbody>
            {roles.map((r) => {
              const ra = rateFor(a, r);
              const rb = rateFor(b, r);
              const delta = (ra != null && rb != null) ? rb - ra : null;
              const color = delta > 0 ? "var(--moss)" : delta < 0 ? "var(--accent)" : "inherit";
              return (
                <tr key={r} className="border-b border-[var(--border)]"
                    data-testid={`plan-compare-row-${r}`}>
                  <td className="py-1.5">{r.replace(/_/g," ")}</td>
                  <td className="py-1.5 text-right font-mono">{fmt(ra)}</td>
                  <td className="py-1.5 text-right font-mono">{fmt(rb)}</td>
                  <td className="py-1.5 text-right font-mono" style={{ color }}>
                    {delta == null ? "—" : `${delta > 0 ? "+" : ""}${fmt(delta)}`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="text-[11px] text-[var(--ink-3)] mt-3">
          {a.start_date}{a.end_date ? ` → ${a.end_date}` : " (current)"} vs {b.start_date}{b.end_date ? ` → ${b.end_date}` : " (current)"}
        </div>
      </div>
    </div>
  );
}

function RatePlanDraft({ draft, setDraft, onCancel, onSave }) {
  const setRate = (idx, patch) => setDraft({
    ...draft,
    rates: draft.rates.map((r, i) => i === idx ? { ...r, ...patch } : r),
  });
  const addRow = () => setDraft({ ...draft, rates: [...draft.rates, { role: "accountant", rate: 0 }] });
  const rmRow = (idx) => setDraft({ ...draft, rates: draft.rates.filter((_, i) => i !== idx) });

  // v68.11 — live impact preview
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);

  const runPreview = async () => {
    setPreviewBusy(true);
    try {
      const body = {
        name: draft.name || "Preview",
        start_date: draft.start_date,
        end_date: draft.end_date || null,
        currency: draft.currency || "AUD",
        rates: draft.rates.filter((r) => r.role && r.rate !== ""),
      };
      const { data } = await axios.post(`${API_BASE}/rate-plans/preview?days=90`, body);
      setPreview(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setPreviewBusy(false); }
  };

  const fmt$ = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  return (
    <div className="card p-4 mb-4 space-y-3" data-testid="rate-plan-draft">
      <div className="grid grid-cols-4 gap-3">
        <div className="col-span-2">
          <label className="overline block mb-1">Name</label>
          <input className={INPUT} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            data-testid="rate-plan-draft-name"/>
        </div>
        <div>
          <label className="overline block mb-1">Start date</label>
          <input className={INPUT} type="date" value={draft.start_date}
            onChange={(e) => setDraft({ ...draft, start_date: e.target.value })}
            data-testid="rate-plan-draft-start"/>
        </div>
        <div>
          <label className="overline block mb-1">End date (optional)</label>
          <input className={INPUT} type="date" value={draft.end_date || ""}
            onChange={(e) => setDraft({ ...draft, end_date: e.target.value })}/>
        </div>
      </div>
      <div className="space-y-1.5">
        <div className="overline">Rates</div>
        {draft.rates.map((r, i) => (
          <div key={i} className="flex gap-2 items-center">
            <select className={INPUT + " flex-1"} value={r.role} onChange={(e) => setRate(i, { role: e.target.value })}
              data-testid={`rate-plan-draft-role-${i}`}>
              {ROLES.map((role) => <option key={role.value} value={role.value}>{role.label}</option>)}
            </select>
            <div className="inline-flex items-center gap-1">
              <span className="text-[var(--ink-3)]">$</span>
              <input className={INPUT + " w-24"} type="number" step="0.01" value={r.rate}
                onChange={(e) => setRate(i, { rate: Number(e.target.value) })}
                data-testid={`rate-plan-draft-rate-${i}`}/>
              <span className="text-[11px] text-[var(--ink-3)]">/h</span>
            </div>
            <button className="text-[var(--ink-3)] hover:text-[var(--accent)]" onClick={() => rmRow(i)}>
              <X size={13}/>
            </button>
          </div>
        ))}
        <button onClick={addRow} className="btn-ghost text-[11px]"><Plus size={11}/> Add rate row</button>
      </div>
      <div className="flex gap-2 pt-2 border-t border-[var(--border)]">
        <button type="button" className="btn-ghost text-[11px]" onClick={runPreview} disabled={previewBusy}
          data-testid="rate-plan-preview-btn">
          {previewBusy ? "Modelling…" : "Preview impact (last 90 days)"}
        </button>
        <div className="flex-1"/>
        <button className="btn-secondary justify-center" onClick={onCancel}>Cancel</button>
        <button className="btn-primary justify-center" onClick={onSave}
          data-testid="rate-plan-draft-save">
          <Save size={12}/> Create plan
        </button>
      </div>

      {preview && (
        <div className="mt-3 border border-[var(--border)] rounded-md p-3 bg-[var(--subtle)]" data-testid="rate-plan-preview-result">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="overline text-[10px]">Modelled impact · last {preview.days} days</div>
            <div className="text-[10px] text-[var(--ink-3)]">{preview.sampled_entries} billable entries sampled</div>
          </div>
          {preview.sampled_entries === 0 ? (
            <div className="text-[12px] text-[var(--ink-3)]">No billable hours in the window — can&apos;t model.</div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2 mb-2">
                <div className="bg-white border border-[var(--border)] rounded p-2">
                  <div className="overline text-[9px] text-[var(--ink-3)]">Current cost</div>
                  <div className="font-mono text-[15px]">{fmt$(preview.current_cost)}</div>
                </div>
                <div className="bg-white border border-[var(--border)] rounded p-2">
                  <div className="overline text-[9px] text-[var(--ink-3)]">Proposed cost</div>
                  <div className="font-mono text-[15px]">{fmt$(preview.proposed_cost)}</div>
                </div>
                <div className="bg-white border border-[var(--border)] rounded p-2">
                  <div className="overline text-[9px] text-[var(--ink-3)]">Delta</div>
                  <div className="font-mono text-[15px]" style={{ color: preview.delta > 0 ? "var(--moss)" : preview.delta < 0 ? "var(--accent)" : "inherit" }}>
                    {preview.delta > 0 ? "+" : ""}{fmt$(preview.delta)}
                    {preview.delta_pct != null && <span className="text-[10px] text-[var(--ink-3)] ml-1">({preview.delta_pct > 0 ? "+" : ""}{preview.delta_pct}%)</span>}
                  </div>
                </div>
              </div>
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-left text-[var(--ink-3)] border-b border-[var(--border)]">
                    <th className="py-1 font-normal">Role</th>
                    <th className="py-1 font-normal text-right">Hours</th>
                    <th className="py-1 font-normal text-right">Current</th>
                    <th className="py-1 font-normal text-right">Proposed</th>
                    <th className="py-1 font-normal text-right">Delta</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.per_role.map((r) => (
                    <tr key={r.role} className="border-b border-[var(--border)]"
                        data-testid={`rate-plan-preview-role-${r.role}`}>
                      <td className="py-1">{r.role.replace(/_/g," ")}</td>
                      <td className="py-1 text-right font-mono">{r.hours}</td>
                      <td className="py-1 text-right font-mono">{fmt$(r.current_cost)}</td>
                      <td className="py-1 text-right font-mono">{fmt$(r.proposed_cost)}</td>
                      <td className="py-1 text-right font-mono"
                          style={{ color: r.delta > 0 ? "var(--moss)" : r.delta < 0 ? "var(--accent)" : "inherit" }}>
                        {r.delta > 0 ? "+" : ""}{fmt$(r.delta)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
}
